/**
 * TokenExchange — Auth Hub SSO callback handler
 *
 * Receives a custom token from the Auth Hub, exchanges it for
 * a Firebase Auth session, then redirects to the stored return path.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';

import { authHubService } from '../../services/AuthHubService';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import Button from '../../components/shared/Button';
import Card from '../../components/shared/Card';

const TokenExchange = () => {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);

  const performExchange = async () => {
    // The service forgets the hub tokens after one attempt, success or failure,
    // so a retry goes back through the hub for fresh ones.
    if (!authHubService.hasAuthToken()) {
      authHubService.redirectToLogin(authHubService.getRedirectPath());
      return;
    }
    try {
      setError(null);
      setRetrying(true);

      await authHubService.handleTokenExchange();
      const redirectPath = authHubService.getRedirectPath();
      navigate(redirectPath, { replace: true });
    } catch (err) {
      console.error('Token exchange failed:', err);
      setError(
        err.message || 'Authentication failed. Please try signing in again.'
      );
    } finally {
      setRetrying(false);
    }
  };

  useEffect(() => {
    if (authHubService.hasAuthToken()) {
      performExchange();
    } else {
      setError('No authentication token found. Please try signing in again.');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <Helmet>
        <title>Signing You In | Finding Your Voice</title>
      </Helmet>

      <div className="min-h-screen flex items-center justify-center bg-courtyard-parchment px-4">
        <motion.div
          className="text-center max-w-md w-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4 }}
        >
          {error ? (
            <Card className="p-8">
              <h2 className="font-display text-xl font-bold text-courtyard-stone mb-3">
                Authentication Error
              </h2>
              <p className="text-sm text-courtyard-stone/80 mb-6">{error}</p>
              <div className="flex flex-col gap-3">
                <Button
                  variant="primary"
                  onClick={performExchange}
                  disabled={retrying}
                >
                  {retrying ? 'Retrying...' : 'Try Again'}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate('/signin')}
                >
                  Back to Sign In
                </Button>
              </div>
            </Card>
          ) : (
            <div>
              <LoadingSpinner message="Completing sign in..." />
            </div>
          )}
        </motion.div>
      </div>
    </>
  );
};

export default TokenExchange;
